/**
 * ModulePanel — a titled frame that auto-renders a module's parameters.
 *
 * `ModulePanel.fromModule(module)` inspects the module's `parameters` array
 * and picks a UI control per parameter: NumericParameter → Knob, EnumParameter
 * → WaveSelect. This means every new module type gets a working UI without
 * touching layout code. When a parameter needs a different control, the
 * module can override via `controlFor()` in a future iteration.
 */

import type { SynthModule } from '@ac-slayer/synth-core';
import type { ModArcController } from '@ac-slayer/synth-core';
import type { MidiBindings } from '@ac-slayer/synth-midi';
import { moduleParamRef } from '@ac-slayer/synth-midi';
import { Knob } from '../components/Knob';
import { WaveSelect } from '../components/WaveSelect';
import { SelectControl } from '../components/SelectControl';
import type { AnyParameter } from '@ac-slayer/synth-core';

/** Enum option count at or below which we render segmented buttons instead
 *  of a dropdown. Five buttons line up cleanly; more look cramped. */
const BUTTONS_OPTION_THRESHOLD = 5;

export interface ModulePanelOptions {
  readonly title: string;
  readonly subtitle?: string;
}

export class ModulePanel {
  readonly element: HTMLElement;
  readonly content: HTMLElement;
  private readonly disposers: Array<() => void> = [];

  constructor(opts: ModulePanelOptions) {
    this.element = document.createElement('section');
    this.element.className = 'module-panel';
    this.element.setAttribute('role', 'group');
    this.element.setAttribute('aria-label', opts.title);

    const header = document.createElement('header');
    header.className = 'module-panel-header';

    const title = document.createElement('h2');
    title.className = 'module-panel-title';
    title.textContent = opts.title;
    header.appendChild(title);

    if (opts.subtitle) {
      const sub = document.createElement('span');
      sub.className = 'module-panel-subtitle label';
      sub.textContent = opts.subtitle;
      header.appendChild(sub);
    }

    this.element.appendChild(header);

    this.content = document.createElement('div');
    this.content.className = 'module-panel-content';
    this.element.appendChild(this.content);
  }

  /** Append pre-built controls. */
  append(...children: HTMLElement[]): this {
    for (const child of children) this.content.appendChild(child);
    return this;
  }

  /** Dispose all child controls that registered with us. */
  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers.length = 0;
  }

  /**
   * Build a panel whose controls are auto-generated from `module.parameters`.
   * The `subtitle` defaults to the module's short id tail, which is nice for
   * telling apart two oscillators at a glance. Pass `modArcs` so the knobs
   * can render their active mod connections as colored arcs.
   */
  static fromModule(
    module: SynthModule,
    opts: {
      subtitle?: string;
      modArcs?: ModArcController;
      midiBindings?: MidiBindings;
    } = {},
  ): ModulePanel {
    const resolvedSubtitle = opts.subtitle ?? module.id.split('-').pop();
    const panel = new ModulePanel({
      title: module.displayName,
      ...(resolvedSubtitle ? { subtitle: resolvedSubtitle } : {}),
    });
    for (const param of module.parameters) {
      panel.addParamControl(param, {
        moduleId: module.id,
        ...(opts.modArcs ? { modArcs: opts.modArcs } : {}),
        ...(opts.midiBindings ? { midiBindings: opts.midiBindings } : {}),
      });
    }
    return panel;
  }

  /**
   * Add a single parameter's control. Public so modules with custom layouts
   * can assemble panels manually when the auto-builder isn't enough.
   */
  addParamControl(
    param: AnyParameter,
    ctx: {
      moduleId?: string;
      modArcs?: ModArcController;
      midiBindings?: MidiBindings;
      /** Override the append target — useful for grouping params under
       *  sub-section containers inside merged panels (SOURCE, MOD). */
      host?: HTMLElement;
    } = {},
  ): this {
    const target = ctx.host ?? this.content;
    if (param.kind === 'numeric') {
      // Build a stable paramRef when both moduleId and bindings are
      // present — so the Knob can offer Learn / Unbind in its
      // right-click menu.
      const paramRef =
        ctx.midiBindings && ctx.moduleId
          ? moduleParamRef(ctx.moduleId, param.name)
          : null;
      const k = new Knob({
        param,
        ...(ctx.moduleId !== undefined ? { moduleId: ctx.moduleId } : {}),
        ...(ctx.modArcs !== undefined ? { modArcs: ctx.modArcs } : {}),
        ...(ctx.midiBindings !== undefined
          ? { midiBindings: ctx.midiBindings }
          : {}),
        ...(paramRef ? { midiParamRef: paramRef } : {}),
      });
      this.disposers.push(() => k.dispose());
      target.appendChild(k.element);
    } else if (param.options.length <= BUTTONS_OPTION_THRESHOLD) {
      const ws = new WaveSelect({ param });
      this.disposers.push(() => ws.dispose());
      target.appendChild(ws.element);
    } else {
      const sel = new SelectControl({ param });
      this.disposers.push(() => sel.dispose());
      target.appendChild(sel.element);
    }
    return this;
  }
}
